import { Action, State, StateContext, Selector } from '@ngxs/store';
import { NewVideoPlaylistAction } from './new-video-playlists.actions';
import { PlaylistService } from '../../services/playlist.service';
import { Playlist, PaginationInfo } from '../../models/models';
import { tap, map, switchMap } from 'rxjs/operators';
import { from, forkJoin, of } from 'rxjs';
import { PlaylistPrepare } from '../../utils/prepare-playlist';
import { ImageUploadService } from '../../services/image-upload.service';
import { MediaTypes } from '../../models/consts';
import { patch, updateItem } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';

type Context = StateContext<NewVideoPlaylistsStateModel>;

export interface NewVideoPlaylistsStateModel {
  playlists: Playlist[];
  isPlaylistsLoaded: boolean;
  hasNextPage: boolean;
  nextCursor: string;
  lastUpdated: Playlist | null;
}

@State<NewVideoPlaylistsStateModel>({
  name: 'newVideoPlaylists',
  defaults: {
    playlists: null,
    isPlaylistsLoaded: false,
    hasNextPage: false,
    nextCursor: null,
    lastUpdated: null
  }
})
@Injectable()
export class NewVideoPlaylistsState {

  constructor(
      private playlistService: PlaylistService,
      private imageUploadService: ImageUploadService
    ) {}

  @Action(NewVideoPlaylistAction.GetNewVideoPlaylists)
  fetch({patchState}: StateContext<NewVideoPlaylistsStateModel>) {
    return this.playlistService.getSystemPlaylistsConnection(null, 20, MediaTypes.VIDEO)
      .pipe(
        tap((paginationInfo: PaginationInfo) => {
          patchState({
            hasNextPage: paginationInfo.pageInfo.hasNextPage,
            nextCursor: paginationInfo.pageInfo.endCursor
          });
        }),
        map(({edges}) => edges.map(({node}) => node)),
        tap((playlists: Playlist[]) => {

          // add artwork to playlist or create collage
          playlists = playlists.map(item => {
            item.artwork = PlaylistPrepare.playlistArtwork(item);
            return item;
          });
          patchState({
            playlists,
            isPlaylistsLoaded: true
          });
        })
      );
  }

  @Action(NewVideoPlaylistAction.GetMoreVideoPlaylists)
  getMore({getState, patchState}: StateContext<NewVideoPlaylistsStateModel>, { count }: NewVideoPlaylistAction.GetMoreVideoPlaylists) {
    const state = getState();
    return this.playlistService.getSystemPlaylistsConnection(state.nextCursor, count || 10, MediaTypes.VIDEO)
      .pipe(
        tap((paginationInfo: PaginationInfo) => {
          patchState({
            hasNextPage: paginationInfo.pageInfo.hasNextPage,
            nextCursor: paginationInfo.pageInfo.endCursor
          });
        }),
        map(({edges}) => edges.map(({node}) => node)),
        tap((playlists: Playlist[]) => {
          // add artwork to playlist or create collage
          playlists = playlists.map(item => {
            item.artwork = PlaylistPrepare.playlistArtwork(item);
            return item;
          });
          
          patchState({
            playlists: [...state.playlists, ...playlists]
          });
        })
      );
  }

  @Action(NewVideoPlaylistAction.CreatePlaylist)
  create({getState, patchState}: StateContext<NewVideoPlaylistsStateModel>, { playlistInput, cover }: NewVideoPlaylistAction.CreatePlaylist) {
    const state = getState();
    let newPlaylist: Playlist;
    return this.playlistService.createNewPlaylist({...playlistInput, mediaType: MediaTypes.VIDEO})
    .pipe(
      tap((playlist) => newPlaylist = playlist),
      switchMap((playlist) => {
        if(!!cover) {
          return from(this.imageUploadService.uploadPlaylistCover(cover, playlist.id))
            .pipe(
              switchMap(() => this.playlistService.updatePlaylistCover(playlist.id)),
              map((logoImageUrl: string) => {
                newPlaylist.logoImageUrl = logoImageUrl;
                return newPlaylist;
              })
            )
        }
        return of(playlist);
      }),
      tap((playlist: Playlist) => {
        playlist.artwork = PlaylistPrepare.playlistArtwork(playlist);
        const playlists = PlaylistPrepare.sortPlaylists([...state.playlists, playlist]);
        patchState({
          playlists,
          lastUpdated: playlist
        });
      })
    );
  }

  @Action(NewVideoPlaylistAction.EditPlaylist)
  edit({getState, patchState}: StateContext<NewVideoPlaylistsStateModel>, { id, playlistInput, cover }: NewVideoPlaylistAction.EditPlaylist) {
    const state = getState();
    let newPlaylist: Playlist;
    return this.playlistService.updateNewPlaylist(id, playlistInput)
    .pipe(
      tap((playlist) => newPlaylist = playlist),
      switchMap((playlist) => {
        if(!!cover) {
        return from(this.imageUploadService.uploadPlaylistCover(cover, id))
          .pipe(
            switchMap(() => this.playlistService.updatePlaylistCover(id)),
            map((logoImageUrl: string) => {
              newPlaylist.logoImageUrl = logoImageUrl;
              return newPlaylist;
            })
          );
        }
        return of(playlist);
      }),
      tap((playlist: Playlist) => {
        playlist.artwork = PlaylistPrepare.playlistArtwork(playlist);
        const playlists = PlaylistPrepare.sortPlaylists([...state.playlists.filter(x => x.id !== playlist.id), playlist]);
        patchState({
          playlists,
          lastUpdated: playlist
        });
      })
    );
  }

  @Action(NewVideoPlaylistAction.DeletePlaylist)
  delete({getState, patchState}: StateContext<NewVideoPlaylistsStateModel>, { id }: NewVideoPlaylistAction.DeletePlaylist) {
    const state = getState();
    return this.playlistService.deleteLibraryPlaylist(id)
    .pipe(
      tap(() => {
        patchState({
          playlists: state.playlists.filter((item) => item.id !== id),
          lastUpdated: null
        });
      })
    );
  }

  @Action(NewVideoPlaylistAction.UpdateTracksInVideo)
  addTracksToPlaylist({getState, patchState}: StateContext<NewVideoPlaylistsStateModel>, {playlistId}: NewVideoPlaylistAction.UpdateTracksInVideo) {
  return this.playlistService.updateTracksForPlaylist(playlistId)
    .pipe(
      tap((playlist: Playlist) => {
        const state = getState();
        const updPlaylist = {
          ...state.playlists.find(({id}) => id === playlist.id),
          trackCount: playlist.trackCount,
          duration: playlist.duration,
          generalGenre: playlist.generalGenre,
          artwork: PlaylistPrepare.videoPlaylistArtwork(playlist),
          updatedAt: playlist.updatedAt
        };
        patchState({
          playlists: PlaylistPrepare.sortPlaylists([...state.playlists.filter(({id}) => id !== playlist.id), updPlaylist]),
          lastUpdated: updPlaylist
        });
      })
    )
  }

  @Action(NewVideoPlaylistAction.RemoveTracksFromPlaylist)
  removeTracksFromPlaylist({getState, patchState}: StateContext<NewVideoPlaylistsStateModel>, {trackIds, playlistId}: NewVideoPlaylistAction.RemoveTracksFromPlaylist) {
    return forkJoin(...trackIds.map(item =>
      this.playlistService.removeTrack(item, playlistId)
    ))
    .pipe(
      switchMap(() => from(this.playlistService.getPlaylist(playlistId))),
      tap((playlist) => {
        const state = getState();
        if (state.isPlaylistsLoaded) {
          const updPlaylist = {
            ...state.playlists.find(x => x.id === playlist.id),
            trackCount: playlist.trackCount,
            duration: playlist.duration,
            generalGenre: playlist.generalGenre,
            artwork: PlaylistPrepare.playlistArtwork(playlist),
            updatedAt: playlist.updatedAt
          };
          patchState({
            playlists: PlaylistPrepare.sortPlaylists([...state.playlists.filter(x => x.id !== playlist.id), updPlaylist]),
            lastUpdated: updPlaylist
          });
        }
      })
    )
  }

  @Action(NewVideoPlaylistAction.ChangePlaylistLibraryState)
  changeLibraryStatus({getState, setState}: StateContext<NewVideoPlaylistsStateModel>, {playlistIds, isInLibrary}: NewVideoPlaylistAction.ChangePlaylistLibraryState) {
    const state = getState();
    if (state.playlists && state.playlists.some(({id}) => playlistIds.includes(id))) {
      const playlistsUpd: Playlist[] = [
        ...state.playlists.map((playlist) => {
          if (playlistIds.find((id) => playlist.id === id)) {
            return {...playlist, isInLibrary};
          }
          return playlist;
        }),
      ]
      setState(
        patch({
          playlists: playlistsUpd
        })
      )
    }
  }

  @Selector()
  static videoPlaylists(state: NewVideoPlaylistsStateModel): Playlist[] {
    return state.playlists;
  }

  @Selector()
  static publishedVideoPlaylists(state: NewVideoPlaylistsStateModel): Playlist[] {
    return state.playlists.filter(x => x.isPublished);
  }

  @Selector()
  static isPlaylistsLoaded(state: NewVideoPlaylistsStateModel): boolean {
    return state.isPlaylistsLoaded;
  }

  @Selector()
  static hasNext(state: NewVideoPlaylistsStateModel): boolean {
    return state.hasNextPage;
  }

  @Selector()
  static lastUpdated(state: NewVideoPlaylistsStateModel): Playlist {
    return state.lastUpdated;
  }
}
